import React, { useState } from 'react';
import tournamentService from '../../services/tournamentService';
import api from '../../services/api';
import toast from 'react-hot-toast';

const TournamentRounds = ({ rounds, tournamentId }) => {
  const [expandedRound, setExpandedRound] = useState(null);
  const [roundDetails, setRoundDetails] = useState({});

  const fetchRoundDetails = async (roundId) => {
    try {
      const response = await api.get(`/tournaments/${tournamentId}/rounds/${roundId}/scores`);
      setRoundDetails(prev => ({
        ...prev,
        [roundId]: response.data.data
      }));
    } catch (error) {
      console.error('Error fetching round details:', error);
      toast.error('Failed to load round details');
    }
  };

  const toggleRoundExpansion = async (round) => {
    if (expandedRound === round.id) {
      // Collapse if already expanded
      setExpandedRound(null);
    } else {
      // Expand and load details if not already loaded
      setExpandedRound(round.id);
      if (!roundDetails[round.id]) {
        await fetchRoundDetails(round.id);
      }
    }
  };

  return (
    <div className="proskins-card" style={{
      padding: 'var(--spacing-lg)',
      background: 'var(--bg-card)',
      borderRadius: 'var(--radius-lg)',
      boxShadow: 'var(--shadow-md)',
      border: '1px solid var(--border-primary)'
    }}>
      <h3 className="proskins-card-title" style={{ 
        marginBottom: 'var(--spacing-lg)',
        color: 'var(--text-primary)',
        fontSize: 'var(--font-size-2xl)',
        fontWeight: '600'
      }}>
        Tournament Rounds
      </h3>
      {rounds.length === 0 ? (
        <div className="proskins-text-center proskins-text-muted" style={{ padding: 'var(--spacing-2xl)' }}>
          No rounds scheduled yet
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-sm)' }}>
          {rounds.map((round) => (
            <div key={round.id} style={{
              border: '1px solid var(--border-primary)',
              borderRadius: 'var(--radius-lg)',
              overflow: 'hidden',
              transition: 'all 0.3s ease',
              background: 'var(--bg-tertiary)'
            }}>
              {/* Round Header - Clickable */}
              <div 
                style={{
                  padding: 'var(--spacing-md) var(--spacing-lg)',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  borderBottom: '1px solid transparent',
                  backgroundColor: round.isCompleted ? 'rgba(0, 255, 136, 0.1)' : 'rgba(255, 170, 0, 0.1)',
                  borderBottomColor: expandedRound === round.id ? 'var(--border-primary)' : 'transparent'
                }}
                onClick={() => toggleRoundExpansion(round)}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-sm)', marginBottom: 'var(--spacing-xs)' }}>
                      <h4 style={{
                        margin: 0,
                        fontSize: 'var(--font-size-xl)',
                        fontWeight: '600',
                        color: 'var(--text-primary)'
                      }}>
                        Round {round.roundNumber}
                      </h4>
                      <span style={{
                        padding: 'var(--spacing-xs) var(--spacing-sm)',
                        borderRadius: 'var(--radius-sm)',
                        fontSize: 'var(--font-size-xs)',
                        fontWeight: '500',
                        textTransform: 'uppercase',
                        backgroundColor: round.isCompleted ? 'var(--success)' : 'var(--warning)',
                        color: 'white'
                      }}>
                        {round.isCompleted ? 'Completed' : 'In Progress'}
                      </span>
                    </div>
                    <div style={{
                      color: 'var(--text-secondary)',
                      fontSize: 'var(--font-size-sm)',
                      lineHeight: '1.4'
                    }}>
                      {round.metadata?.holes || 18} holes • Par {round.metadata?.par || 72}
                      {round.currentHole && ` • Current: Hole ${round.currentHole}`}
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-md)' }}>
                    <div style={{ textAlign: 'right' }}>
                      <span style={{
                        fontWeight: '600',
                        color: 'var(--info)',
                        fontSize: 'var(--font-size-sm)'
                      }}>
                        {round._count?.scores || 0} scores
                      </span>
                    </div>
                    <div style={{
                      width: '24px',
                      height: '24px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <span style={{
                        fontSize: 'var(--font-size-sm)',
                        color: 'var(--text-muted)',
                        transition: 'transform 0.3s ease',
                        transform: expandedRound === round.id ? 'rotate(180deg)' : 'rotate(0deg)'
                      }}>
                        ▼
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Round Details - Expandable */}
              {expandedRound === round.id && (
                <div style={{
                  padding: 'var(--spacing-lg)',
                  backgroundColor: 'var(--bg-card)',
                  borderTop: '1px solid var(--border-primary)',
                  animation: 'slideDown 0.3s ease-out'
                }}>
                  <div style={{ marginBottom: 'var(--spacing-xl)' }}>
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                      gap: 'var(--spacing-md)'
                    }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-xs)' }}>
                        <span style={{
                          fontSize: 'var(--font-size-sm)',
                          color: 'var(--text-secondary)',
                          fontWeight: '500'
                        }}>Status:</span>
                        <span style={{
                          fontSize: 'var(--font-size-base)',
                          color: 'var(--text-primary)',
                          fontWeight: '600'
                        }}>{round.status}</span>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-xs)' }}>
                        <span style={{
                          fontSize: 'var(--font-size-sm)',
                          color: 'var(--text-secondary)',
                          fontWeight: '500'
                        }}>Total Holes:</span>
                        <span style={{
                          fontSize: 'var(--font-size-base)',
                          color: 'var(--text-primary)',
                          fontWeight: '600'
                        }}>{round.totalHoles}</span>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-xs)' }}>
                        <span style={{
                          fontSize: 'var(--font-size-sm)',
                          color: 'var(--text-secondary)',
                          fontWeight: '500'
                        }}>Par Total:</span>
                        <span style={{
                          fontSize: 'var(--font-size-base)',
                          color: 'var(--text-primary)',
                          fontWeight: '600'
                        }}>{round.parTotal}</span>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-xs)' }}>
                        <span style={{
                          fontSize: 'var(--font-size-sm)',
                          color: 'var(--text-secondary)',
                          fontWeight: '500'
                        }}>Current Hole:</span>
                        <span style={{
                          fontSize: 'var(--font-size-base)',
                          color: 'var(--text-primary)',
                          fontWeight: '600'
                        }}>{round.currentHole || 'N/A'}</span>
                      </div>
                      {round.startDate && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-xs)' }}>
                          <span style={{
                            fontSize: 'var(--font-size-sm)',
                            color: 'var(--text-secondary)',
                            fontWeight: '500'
                          }}>Start Date:</span>
                          <span style={{
                            fontSize: 'var(--font-size-base)',
                            color: 'var(--text-primary)',
                            fontWeight: '600'
                          }}>{tournamentService.formatDateTime(round.startDate)}</span>
                        </div>
                      )}
                      {round.endDate && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-xs)' }}>
                          <span style={{
                            fontSize: 'var(--font-size-sm)',
                            color: 'var(--text-secondary)',
                            fontWeight: '500'
                          }}>End Date:</span>
                          <span style={{
                            fontSize: 'var(--font-size-base)',
                            color: 'var(--text-primary)',
                            fontWeight: '600'
                          }}>{tournamentService.formatDateTime(round.endDate)}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Scores Section */}
                  <div style={{ marginTop: 'var(--spacing-xl)' }}>
                    <h5 style={{
                      fontSize: 'var(--font-size-lg)',
                      fontWeight: '600',
                      color: 'var(--text-primary)',
                      margin: '0 0 var(--spacing-md) 0',
                      paddingBottom: 'var(--spacing-xs)',
                      borderBottom: '2px solid var(--border-primary)'
                    }}>Player Scorecard</h5>
                    {!roundDetails[round.id] ? (
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 'var(--spacing-sm)',
                        padding: 'var(--spacing-lg)',
                        justifyContent: 'center',
                        color: 'var(--text-muted)'
                      }}>
                        <div className="proskins-loading" style={{ width: '16px', height: '16px' }}></div>
                        <span>Loading scores...</span>
                      </div>
                    ) : roundDetails[round.id].scores && roundDetails[round.id].scores.length > 0 ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-xl)' }}>
                        {/* Player Summary Table */}
                        <div style={{ marginBottom: 'var(--spacing-md)' }}>
                          <div style={{ overflowX: 'auto' }}>
                            <table style={{
                              width: '100%',
                              borderCollapse: 'collapse',
                              borderRadius: 'var(--radius-sm)',
                              overflow: 'hidden',
                              boxShadow: 'var(--shadow-sm)',
                              background: 'var(--bg-tertiary)',
                              border: '1px solid var(--border-primary)'
                            }}>
                              <thead>
                                <tr>
                                  <th style={{
                                    backgroundColor: 'var(--bg-tertiary)',
                                    padding: 'var(--spacing-sm) var(--spacing-md)',
                                    textAlign: 'left',
                                    fontWeight: '600',
                                    color: 'var(--text-primary)',
                                    fontSize: 'var(--font-size-sm)',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px'
                                  }}>Player</th>
                                  <th style={{
                                    backgroundColor: 'var(--bg-tertiary)',
                                    padding: 'var(--spacing-sm) var(--spacing-md)',
                                    textAlign: 'center',
                                    fontWeight: '600',
                                    color: 'var(--text-primary)',
                                    fontSize: 'var(--font-size-sm)',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px'
                                  }}>Total Score</th>
                                  <th style={{
                                    backgroundColor: 'var(--bg-tertiary)',
                                    padding: 'var(--spacing-sm) var(--spacing-md)',
                                    textAlign: 'center',
                                    fontWeight: '600',
                                    color: 'var(--text-primary)',
                                    fontSize: 'var(--font-size-sm)',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px'
                                  }}>Holes Played</th>
                                  <th style={{
                                    backgroundColor: 'var(--bg-tertiary)',
                                    padding: 'var(--spacing-sm) var(--spacing-md)',
                                    textAlign: 'center',
                                    fontWeight: '600',
                                    color: 'var(--text-primary)',
                                    fontSize: 'var(--font-size-sm)',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px'
                                  }}>Avg Score</th>
                                  <th style={{
                                    backgroundColor: 'var(--bg-tertiary)',
                                    padding: 'var(--spacing-sm) var(--spacing-md)',
                                    textAlign: 'center',
                                    fontWeight: '600',
                                    color: 'var(--text-primary)',
                                    fontSize: 'var(--font-size-sm)',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px'
                                  }}>Status</th>
                                </tr>
                              </thead>
                              <tbody>
                                {roundDetails[round.id].scores.map((score, index) => (
                                  <tr key={index} style={{
                                    borderBottom: '1px solid var(--border-primary)',
                                    transition: 'background-color 0.2s ease'
                                  }}>
                                    <td style={{ padding: 'var(--spacing-sm) var(--spacing-md)' }}>
                                      <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                        <strong style={{ color: 'var(--text-primary)' }}>{score.golferName}</strong>
                                        <small style={{ color: 'var(--text-muted)', fontSize: 'var(--font-size-xs)' }}>
                                          Rank #{score.golfer?.worldRanking || 'N/A'}
                                        </small>
                                      </div>
                                    </td>
                                    <td style={{ 
                                      padding: 'var(--spacing-sm) var(--spacing-md)', 
                                      textAlign: 'center',
                                      fontWeight: '700',
                                      color: 'var(--info)',
                                      fontSize: 'var(--font-size-lg)'
                                    }}>{score.totalScore}</td>
                                    <td style={{ 
                                      padding: 'var(--spacing-sm) var(--spacing-md)', 
                                      textAlign: 'center',
                                      color: 'var(--text-muted)'
                                    }}>{score.holesPlayed}/18</td>
                                    <td style={{ 
                                      padding: 'var(--spacing-sm) var(--spacing-md)', 
                                      textAlign: 'center',
                                      fontWeight: '600',
                                      color: 'var(--text-primary)'
                                    }}>{score.averageScore}</td>
                                    <td style={{ padding: 'var(--spacing-sm) var(--spacing-md)', textAlign: 'center' }}>
                                      <span style={{
                                        padding: 'var(--spacing-xs) var(--spacing-sm)',
                                        borderRadius: 'var(--radius-sm)',
                                        fontSize: 'var(--font-size-xs)',
                                        fontWeight: '500',
                                        textTransform: 'uppercase',
                                        backgroundColor: score.isCompleted ? 'var(--success)' : 'var(--warning)',
                                        color: 'white'
                                      }}>
                                        {score.isCompleted ? 'Completed' : 'In Progress'}
                                      </span>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>

                        {/* Detailed Hole-by-Hole Scorecard */}
                        <div style={{
                          background: 'var(--bg-tertiary)',
                          borderRadius: 'var(--radius-lg)',
                          padding: 'var(--spacing-lg)',
                          border: '1px solid var(--border-primary)'
                        }}>
                          <h6 style={{
                            fontSize: 'var(--font-size-lg)',
                            fontWeight: '600',
                            color: 'var(--text-primary)',
                            margin: '0 0 var(--spacing-lg) 0',
                            paddingBottom: 'var(--spacing-xs)',
                            borderBottom: '2px solid var(--border-primary)'
                          }}>Hole-by-Hole Scores</h6>
                          
                          {roundDetails[round.id].scores.map((player, playerIndex) => (
                            <div key={playerIndex} style={{
                              background: 'var(--bg-card)',
                              borderRadius: 'var(--radius-lg)',
                              padding: 'var(--spacing-md)',
                              marginBottom: 'var(--spacing-lg)',
                              boxShadow: 'var(--shadow-sm)',
                              border: '1px solid var(--border-primary)'
                            }}>
                              <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                marginBottom: 'var(--spacing-md)',
                                paddingBottom: 'var(--spacing-sm)',
                                borderBottom: '1px solid var(--border-primary)'
                              }}>
                                <h4 style={{ margin: 0, color: 'var(--text-primary)', fontSize: 'var(--font-size-xl)' }}>
                                  {player.golferName}
                                </h4>
                                <span style={{
                                  fontSize: 'var(--font-size-lg)',
                                  fontWeight: '700',
                                  color: 'var(--info)',
                                  background: 'rgba(0, 123, 255, 0.1)',
                                  padding: 'var(--spacing-xs) var(--spacing-sm)',
                                  borderRadius: 'var(--radius-xl)'
                                }}>
                                  Total: {player.totalScore}
                                </span>
                              </div>
                              
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-md)' }}>
                                {/* Front 9 */}
                                <div style={{ border: '1px solid var(--border-primary)', borderRadius: 'var(--radius-sm)', overflow: 'hidden' }}>
                                  <div style={{
                                    background: 'var(--success)',
                                    color: 'white',
                                    padding: 'var(--spacing-xs) var(--spacing-md)',
                                    fontWeight: '600',
                                    fontSize: 'var(--font-size-sm)',
                                    textAlign: 'center'
                                  }}>Front 9</div>
                                  <div style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(9, 1fr)',
                                    gap: 0
                                  }}>
                                    {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(holeNum => {
                                      const holeData = player.holes?.find(h => h.holeNumber === holeNum);
                                      const isEagleOrBetter = holeData && holeData.par && (holeData.par - holeData.score >= 2);
                                      return (
                                        <div key={holeNum} style={{
                                          borderRight: '1px solid var(--border-primary)',
                                          padding: 'var(--spacing-sm) var(--spacing-xs)',
                                          textAlign: 'center',
                                          background: holeData?.isSkin ? 'rgba(255, 170, 0, 0.1)' : 
                                                     isEagleOrBetter ? 'rgba(0, 255, 136, 0.1)' : 'var(--bg-card)',
                                          transition: 'background-color 0.2s ease'
                                        }}>
                                          <div style={{
                                            fontSize: 'var(--font-size-xs)',
                                            color: 'var(--text-muted)',
                                            marginBottom: 'var(--spacing-xs)',
                                            fontWeight: '500'
                                          }}>H{holeNum}</div>
                                          <div style={{
                                            fontSize: 'var(--font-size-lg)',
                                            fontWeight: '700',
                                            color: 'var(--text-primary)',
                                            marginBottom: '2px',
                                            position: 'relative'
                                          }}>
                                            {holeData ? holeData.score : '-'}
                                            {holeData?.isSkin && <span style={{ color: 'var(--warning)', fontSize: 'var(--font-size-xs)', marginLeft: '2px' }}>★</span>}
                                            {isEagleOrBetter && <span style={{ color: 'var(--success)', fontSize: 'var(--font-size-sm)', marginLeft: '2px' }}>🦅</span>}
                                          </div>
                                          <div style={{
                                            fontSize: 'var(--font-size-xs)',
                                            color: 'var(--text-muted)',
                                            fontWeight: '500'
                                          }}>Par {holeData?.par || 4}</div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                  <div style={{
                                    background: 'var(--bg-tertiary)',
                                    padding: 'var(--spacing-sm) var(--spacing-md)',
                                    fontWeight: '600',
                                    color: 'var(--text-primary)',
                                    borderTop: '1px solid var(--border-primary)',
                                    textAlign: 'center'
                                  }}>
                                    Out: {player.holes?.filter(h => h.holeNumber <= 9)
                                      .reduce((sum, h) => sum + h.score, 0) || 0}
                                  </div>
                                </div>

                                {/* Back 9 */}
                                <div style={{ border: '1px solid var(--border-primary)', borderRadius: 'var(--radius-sm)', overflow: 'hidden' }}>
                                  <div style={{
                                    background: 'var(--error)',
                                    color: 'white',
                                    padding: 'var(--spacing-xs) var(--spacing-md)',
                                    fontWeight: '600',
                                    fontSize: 'var(--font-size-sm)',
                                    textAlign: 'center'
                                  }}>Back 9</div>
                                  <div style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(9, 1fr)',
                                    gap: 0
                                  }}>
                                    {[10, 11, 12, 13, 14, 15, 16, 17, 18].map(holeNum => {
                                      const holeData = player.holes?.find(h => h.holeNumber === holeNum);
                                      const isEagleOrBetter = holeData && holeData.par && (holeData.par - holeData.score >= 2);
                                      return (
                                        <div key={holeNum} style={{
                                          borderRight: '1px solid var(--border-primary)',
                                          padding: 'var(--spacing-sm) var(--spacing-xs)',
                                          textAlign: 'center',
                                          background: holeData?.isSkin ? 'rgba(255, 170, 0, 0.1)' : 
                                                     isEagleOrBetter ? 'rgba(0, 255, 136, 0.1)' : 'var(--bg-card)',
                                          transition: 'background-color 0.2s ease'
                                        }}>
                                          <div style={{
                                            fontSize: 'var(--font-size-xs)',
                                            color: 'var(--text-muted)',
                                            marginBottom: 'var(--spacing-xs)',
                                            fontWeight: '500'
                                          }}>H{holeNum}</div>
                                          <div style={{
                                            fontSize: 'var(--font-size-lg)',
                                            fontWeight: '700',
                                            color: 'var(--text-primary)',
                                            marginBottom: '2px',
                                            position: 'relative'
                                          }}>
                                            {holeData ? holeData.score : '-'}
                                            {holeData?.isSkin && <span style={{ color: 'var(--warning)', fontSize: 'var(--font-size-xs)', marginLeft: '2px' }}>★</span>}
                                            {isEagleOrBetter && <span style={{ color: 'var(--success)', fontSize: 'var(--font-size-sm)', marginLeft: '2px' }}>🦅</span>}
                                          </div>
                                          <div style={{
                                            fontSize: 'var(--font-size-xs)',
                                            color: 'var(--text-muted)',
                                            fontWeight: '500'
                                          }}>Par {holeData?.par || 4}</div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                  <div style={{
                                    background: 'var(--bg-tertiary)',
                                    padding: 'var(--spacing-sm) var(--spacing-md)',
                                    fontWeight: '600',
                                    color: 'var(--text-primary)',
                                    borderTop: '1px solid var(--border-primary)',
                                    textAlign: 'center'
                                  }}>
                                    In: {player.holes?.filter(h => h.holeNumber > 9)
                                      .reduce((sum, h) => sum + h.score, 0) || 0}
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <div style={{
                        textAlign: 'center',
                        padding: 'var(--spacing-2xl) var(--spacing-lg)',
                        color: 'var(--text-muted)',
                        fontStyle: 'italic',
                        background: 'var(--bg-tertiary)',
                        borderRadius: 'var(--radius-sm)',
                        border: '1px solid var(--border-primary)'
                      }}>
                        No scores available for this round yet
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default TournamentRounds;